// =============================================================================
// SHANIZ GAMING ADMIN - THEME FACTORY
// =============================================================================

import { createTheme, Theme } from '@mui/material';

// v3 brand: dark-first + confident gold (matches the mobile #3 app).
// Dark surfaces mirror the mobile theme (near-black #0F0F14 / cards #1A1A24).
const dark = {
  primary: '#F5C518',        // brand gold — accents, buttons, links
  primaryLight: '#FFD84D',
  primaryDark: '#D9AC0F',
  primaryContrast: '#0C0A09', // near-black ink on gold
  secondary: '#94A3B8',
  accent: '#AAAAAE',
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#3b82f6',
  background: '#0F0F14',
  backgroundAlt: '#15151B',
  surface: '#1A1A24',
  surfaceAlt: '#22222E',
  textPrimary: '#F0F0F4',
  textSecondary: '#AAAAAE',
  textMuted: '#66666E',
  divider: '#2A2A33',
};

const light = {
  primary: '#B8860B',        // deeper gold, readable on white
  primaryLight: '#D9AC0F',
  primaryDark: '#8C6608',
  primaryContrast: '#ffffff',
  secondary: '#64748b',
  accent: '#94a3b8',
  success: '#16a34a',
  warning: '#d97706',
  error: '#dc2626',
  info: '#2563eb',
  background: '#f8fafc',
  backgroundAlt: '#f1f5f9',
  surface: '#ffffff',
  surfaceAlt: '#f1f5f9',
  textPrimary: '#0f172a',
  textSecondary: '#334155',
  textMuted: '#64748b',
  divider: '#e2e8f0',
};

export type PaletteMode = 'light' | 'dark';

export function getTheme(mode: PaletteMode): Theme {
  const c = mode === 'dark' ? dark : light;
  return createTheme({
    palette: {
      mode,
      primary: { main: c.primary, light: c.primaryLight, dark: c.primaryDark, contrastText: c.primaryContrast },
      secondary: { main: c.secondary, light: c.accent },
      success: { main: c.success },
      warning: { main: c.warning },
      error: { main: c.error },
      info: { main: c.info },
      background: { default: c.background, paper: c.surface },
      text: { primary: c.textPrimary, secondary: c.textSecondary },
      divider: c.divider,
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h4: { fontWeight: 700 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: c.background, color: c.textPrimary },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: c.surface,
            boxShadow: mode === 'dark'
              ? '0 4px 20px rgba(0,0,0,0.4)'
              : '0 1px 3px rgba(15,23,42,0.08), 0 1px 2px rgba(15,23,42,0.04)',
            borderRadius: 16,
            border: `1px solid ${c.divider}`,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundColor: c.surface, backgroundImage: 'none' },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600, borderRadius: 12 },
          contained: {
            backgroundColor: c.primary,
            color: c.primaryContrast,
            '&:hover': { backgroundColor: c.primaryDark },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: c.surfaceAlt,
            '& .MuiTableCell-head': {
              fontWeight: 600,
              color: c.textPrimary,
              borderBottom: `1px solid ${c.divider}`,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: `1px solid ${c.divider}`, color: c.textSecondary },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': { backgroundColor: `${c.surfaceAlt} !important` },
          },
        },
      },
      MuiChip: { styleOverrides: { root: { borderRadius: 8 } } },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: mode === 'dark' ? c.backgroundAlt : c.surface,
              borderRadius: 10,
              '& fieldset': { borderColor: c.divider },
              '&:hover fieldset': { borderColor: c.primary },
              '&.Mui-focused fieldset': { borderColor: c.primary, borderWidth: 1 },
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? c.backgroundAlt : c.surface,
            borderRadius: 10,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { backgroundColor: c.surface, borderRadius: 16 },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: c.surface,
            borderRadius: 12,
            border: `1px solid ${c.divider}`,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: c.surface,
            color: c.textPrimary,
            boxShadow: 'none',
            borderBottom: `1px solid ${c.divider}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: c.background,
            borderRight: `1px solid ${c.divider}`,
          },
        },
      },
    },
  });
}
